import toast from "@/components/ui/toast";
import {
  GetKeyMessageGroupsForSegment,
  type GetKeyMessageGroupsForSegmentVariablesType,
} from "@/data-access/news-feeds/queries/key-message-queries";
import {
  CreateShareVoiceSegment,
  DeleteShareVoiceSegment,
  GetShareVoiceSegments,
  UpdateShareVoiceSegment,
} from "@/data-access/share-of-voice/queries/segment-queries";
import { gb } from "@/lib/feature-management";
import { client } from "@/lib/urqlProvider";
import useSegmentStore from "./segment.slice";

export const createSegment = async (): Promise<boolean> => {
  const { name, color, keyMessageId, feedId, savedViewId } =
    useSegmentStore.getState();

  if (!name || !color || !feedId) {
    return false;
  }

  useSegmentStore.setState(
    { isCreatingSegment: true },
    false,
    "[User] Segment: Creating segment",
  );

  try {
    const enableSOVExp = gb.getFeatureValue("enable-sov-exp", false);
    const result = await client
      .mutation(CreateShareVoiceSegment, {
        input: {
          name,
          hexColor: color,
          ...(enableSOVExp
            ? { savedViewId: savedViewId || null }
            : { keyMessageId: keyMessageId || null }),
          feedId,
        },
      })
      .toPromise();

    if (result.error) {
      toast.error("Failed to create segment.");
      useSegmentStore.setState(
        { isCreatingSegment: false },
        false,
        "[User] Segment: Create segment error",
      );
      return false;
    }

    const createdSegment = result.data?.createShareVoiceSegment;
    if (
      !createdSegment ||
      createdSegment.__typename !== "ShareVoiceSegmentType"
    ) {
      toast.error("Failed to create segment.");
      useSegmentStore.setState({ isCreatingSegment: false });
      return false;
    }

    // Add the newly created segment to the existing segments set
    const { segments: currentSegments } = useSegmentStore.getState();
    const segmentsArray = [...currentSegments, createdSegment];

    useSegmentStore.setState(
      {
        isCreatingSegment: false,
        createSegmentState: "complete",
        isCreateOrEditSegmentModalOpen: false,
        segments: new Set(segmentsArray),
      },
      false,
      "[User] Segment: Create segment success",
    );

    // Reset segment state after successful creation
    useSegmentStore.getState().resetSegment();

    toast({
      type: "success",
      title: "Segment created!",
      description: `Segment "${name}" has been created successfully.`,
    });

    return true;
  } catch (error) {
    console.error("Error creating segment:", error);
    toast.error("An error occurred while creating the segment.");
    useSegmentStore.setState({ isCreatingSegment: false });
    return false;
  }
};

/**
 * Fetches all segments
 */
export const getSegments = async (): Promise<boolean> => {
  useSegmentStore.setState(
    { isFetchingSegments: true },
    false,
    "[User] Segment: Fetching segments",
  );

  try {
    const result = await client.query(GetShareVoiceSegments, {}).toPromise();

    if (result.error) {
      console.error("Error fetching segments:", result.error);
      toast.error("Failed to fetch segments.");
      useSegmentStore.setState(
        { isFetchingSegments: false },
        false,
        "[User] Segment: Fetch segments error",
      );
      return false;
    }

    const segments = result.data?.shareVoiceSegments || [];

    useSegmentStore.setState(
      {
        isFetchingSegments: false,
        segments: new Set(segments),
      },
      false,
      "[User] Segment: Fetch segments success",
    );

    return true;
  } catch (error) {
    console.error("Error fetching segments:", error);
    toast.error("An error occurred while fetching segments.");
    useSegmentStore.setState(
      { isFetchingSegments: false },
      false,
      "[User] Segment: Fetch segments error",
    );
    return false;
  }
};

/**
 * Fetches key message groups for segments
 */
export const getKeyMessageGroupsForSegment = async (
  // feedId is string (ID) in the app but KeyMessageGroupByFeedFilter.feedId is still Int!
  { feedId }: { feedId: string },
): Promise<boolean> => {
  const filters: GetKeyMessageGroupsForSegmentVariablesType["filters"] = {
    feedId,
  };
  useSegmentStore.setState(
    { isFetchingKeyMessagesAndGroups: true },
    false,
    "[User] Segment: Fetching key message groups",
  );

  try {
    const result = await client
      .query(GetKeyMessageGroupsForSegment, { filters })
      .toPromise();

    if (result.error) {
      console.error("Error fetching key message groups:", result.error);
      toast.error("Failed to fetch key topic groups.");
      useSegmentStore.setState(
        { isFetchingKeyMessagesAndGroups: false },
        false,
        "[User] Segment: Fetch key message groups error",
      );
      return false;
    }

    const keyMessageGroups = result.data?.keyMessageGroups || [];

    useSegmentStore.setState(
      {
        isFetchingKeyMessagesAndGroups: false,
        keyMessagesAndGroups: keyMessageGroups,
      },
      false,
      "[User] Segment: Fetch key message groups success",
    );

    return true;
  } catch (error) {
    console.error("Error fetching key message groups:", error);
    toast.error("An error occurred while fetching key topic groups.");
    useSegmentStore.setState(
      { isFetchingKeyMessagesAndGroups: false },
      false,
      "[User] Segment: Fetch key message groups error",
    );
    return false;
  }
};

/**
 * Updates an existing segment
 */
export const updateSegment = async (
  input: {
    id: string;
    name?: string;
    hexColor?: string;
    isVisible?: boolean;
  },
  options?: {
    showSuccessToast?: boolean;
  },
): Promise<boolean> => {
  try {
    useSegmentStore.setState(
      { isUpdatingSegment: true },
      false,
      "[User] Segment: Updating segment",
    );

    const result = await client
      .mutation(UpdateShareVoiceSegment, {
        input,
      })
      .toPromise();

    if (result.error) {
      toast.error("Failed to update segment.");
      useSegmentStore.setState(
        { isUpdatingSegment: false },
        false,
        "[User] Segment: Update error",
      );
      return false;
    }

    const updatedSegment = result.data?.updateShareVoiceSegment;
    if (
      !updatedSegment ||
      updatedSegment.__typename !== "ShareVoiceSegmentType"
    ) {
      toast.error("Failed to update segment.");
      useSegmentStore.setState(
        { isUpdatingSegment: false },
        false,
        "[User] Segment: Update error",
      );
      return false;
    }

    // Update the segment in the existing segments set
    const { segments: currentSegments } = useSegmentStore.getState();
    const segmentsArray = [...currentSegments].map((segment) =>
      segment.id === input.id ? updatedSegment : segment,
    );

    useSegmentStore.setState(
      {
        segments: new Set(segmentsArray),
        isUpdatingSegment: false,
        isCreateOrEditSegmentModalOpen: false,
      },
      false,
      "[User] Segment: Update success",
    );

    // Reset segment state after successful update
    useSegmentStore.getState().resetSegment();

    // Only show success toast if explicitly requested
    if (options?.showSuccessToast !== false) {
      const segmentName = input.name || "Segment";
      toast({
        type: "success",
        title: "Segment updated!",
        description: `Segment "${segmentName}" has been updated successfully.`,
      });
    }

    return true;
  } catch (error) {
    console.error("Error updating segment:", error);
    toast.error("An error occurred while updating segment.");
    useSegmentStore.setState(
      { isUpdatingSegment: false },
      false,
      "[User] Segment: Update error",
    );
    return false;
  }
};

/**
 * Deletes a segment
 */
export const deleteSegment = async (
  itemId: string,
  segmentName: string,
): Promise<boolean> => {
  try {
    useSegmentStore.setState(
      { isDeletingSegment: true },
      false,
      "[User] Segment: Deleting segment",
    );

    const result = await client
      .mutation(DeleteShareVoiceSegment, {
        input: {
          itemId,
        },
      })
      .toPromise();

    if (result.error) {
      toast.error("Failed to delete segment.");
      useSegmentStore.setState(
        { isDeletingSegment: false },
        false,
        "[User] Segment: Delete error",
      );
      return false;
    }

    const deleteResult = result.data?.deleteShareVoiceSegment;
    if (!deleteResult) {
      toast.error("Failed to delete segment.");
      useSegmentStore.setState(
        { isDeletingSegment: false },
        false,
        "[User] Segment: Delete error",
      );
      return false;
    }

    // Handle the union type response
    if (deleteResult.__typename === "OperationInfo") {
      const errorMessage =
        deleteResult.messages?.[0]?.message || "Failed to delete segment.";
      toast.error(errorMessage);
      useSegmentStore.setState(
        { isDeletingSegment: false },
        false,
        "[User] Segment: Delete error",
      );
      return false;
    }

    // Success case - remove the segment from the store
    const { segments: currentSegments } = useSegmentStore.getState();
    const updatedSegments = [...currentSegments].filter(
      (segment) => segment.id !== itemId,
    );

    useSegmentStore.setState(
      {
        segments: new Set(updatedSegments),
        isDeletingSegment: false,
      },
      false,
      "[User] Segment: Delete success",
    );

    // Reset delete segment state after successful deletion
    useSegmentStore.getState().resetDeleteSegment();

    toast({
      type: "success",
      title: "Segment deleted!",
      description: `Segment "${segmentName}" has been deleted successfully.`,
    });

    return true;
  } catch (error) {
    console.error("Error deleting segment:", error);
    toast.error("An error occurred while deleting segment.");
    useSegmentStore.setState(
      { isDeletingSegment: false },
      false,
      "[User] Segment: Delete error",
    );
    return false;
  }
};
